import React from 'react';

const FIELD_TYPES = [
  { value: 'text', label: 'Testo breve' },
  { value: 'textarea', label: 'Testo lungo' },
  { value: 'radio', label: 'Scelta singola' },
  { value: 'checkbox', label: 'Scelta multipla' }
];

function generateId() {
  return 'field_' + Math.random().toString(36).substr(2, 9);
}

/**
 * Get the display value from an option (supports both formats).
 */
function getOptionValue(option) {
  if (typeof option === 'string') return option;
  return option.value || '';
}

/**
 * Get the includeInExport flag from an option.
 */
function getOptionExport(option) {
  if (typeof option === 'string') return false;
  return option.includeInExport || false;
}

function FormBuilder({ fields, onChange }) {
  const addField = () => {
    const newField = {
      id: generateId(),
      type: 'text',
      label: '',
      required: false,
      placeholder: '',
      includeInExport: false,
      options: []
    };
    onChange([...fields, newField]);
  };

  const removeField = (index) => {
    const newFields = fields.filter((_, i) => i !== index);
    onChange(newFields);
  };

  const updateField = (index, updates) => {
    const newFields = [...fields];
    newFields[index] = { ...newFields[index], ...updates };
    onChange(newFields);
  };

  const moveField = (index, direction) => {
    const newFields = [...fields];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= fields.length) return;
    [newFields[index], newFields[newIndex]] = [newFields[newIndex], newFields[index]];
    onChange(newFields);
  };

  const addOption = (fieldIndex) => {
    const newFields = [...fields];
    const options = newFields[fieldIndex].options || [];
    // Add new option in object format
    newFields[fieldIndex].options = [...options, { value: '', includeInExport: false }];
    onChange(newFields);
  };

  const updateOption = (fieldIndex, optionIndex, value, includeInExport) => {
    const newFields = [...fields];
    const currentOption = newFields[fieldIndex].options[optionIndex];
    // Preserve existing includeInExport if not explicitly provided
    const currentExport = getOptionExport(currentOption);
    newFields[fieldIndex].options[optionIndex] = {
      value: value,
      includeInExport: includeInExport !== undefined ? includeInExport : currentExport
    };
    onChange(newFields);
  };

  const updateOptionExport = (fieldIndex, optionIndex, includeInExport) => {
    const newFields = [...fields];
    const currentOption = newFields[fieldIndex].options[optionIndex];
    const currentValue = getOptionValue(currentOption);
    newFields[fieldIndex].options[optionIndex] = {
      value: currentValue,
      includeInExport: includeInExport
    };
    onChange(newFields);
  };

  const removeOption = (fieldIndex, optionIndex) => {
    const newFields = [...fields];
    newFields[fieldIndex].options = newFields[fieldIndex].options.filter((_, i) => i !== optionIndex);
    onChange(newFields);
  };

  const needsOptions = (type) => type === 'radio' || type === 'checkbox';

  return (
    <div className="space-y-4">
      {fields.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-300 rounded-lg">
          <p className="mb-2">Nessun campo nel modulo</p>
          <p className="text-sm">Aggiungi campi per creare il modulo di iscrizione</p>
        </div>
      ) : (
        <div className="space-y-4">
          {fields.map((field, index) => (
            <div key={field.id} className="border border-gray-300 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Field Label */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Domanda *
                    </label>
                    <input
                      type="text"
                      value={field.label}
                      onChange={(e) => updateField(index, { label: e.target.value })}
                      placeholder="Es: Hai mai fatto qualche ferrata?"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  {/* Field Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Tipo di campo
                    </label>
                    <select
                      value={field.type}
                      onChange={(e) => updateField(index, {
                        type: e.target.value,
                        options: needsOptions(e.target.value) ? (field.options?.length ? field.options : [{ value: '', includeInExport: false }]) : []
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      {FIELD_TYPES.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Move and Delete buttons */}
                <div className="flex flex-col gap-1">
                  <button
                    type="button"
                    onClick={() => moveField(index, -1)}
                    disabled={index === 0}
                    className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    title="Sposta su"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => moveField(index, 1)}
                    disabled={index === fields.length - 1}
                    className="p-1 text-gray-500 hover:text-gray-700 disabled:opacity-30"
                    title="Sposta giù"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => removeField(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                    title="Elimina campo"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Placeholder for text fields */}
              {(field.type === 'text' || field.type === 'textarea') && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Testo placeholder (opzionale)
                  </label>
                  <input
                    type="text"
                    value={field.placeholder || ''}
                    onChange={(e) => updateField(index, { placeholder: e.target.value })}
                    placeholder="Es: Descrivi la tua esperienza..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              )}

              {/* Options for radio/checkbox */}
              {needsOptions(field.type) && (
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Opzioni
                  </label>
                  <div className="space-y-2">
                    {(field.options || []).map((option, optionIndex) => {
                      const optionValue = getOptionValue(option);
                      const optionExport = getOptionExport(option);

                      return (
                        <div key={optionIndex} className="flex items-center gap-2">
                          <input
                            type="text"
                            value={optionValue}
                            onChange={(e) => updateOption(index, optionIndex, e.target.value)}
                            placeholder={`Opzione ${optionIndex + 1}`}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                />
                          <label
                            className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer select-none"
                            title="Includi questa opzione nella lista partecipanti esportata"
                          >
                            <input
                              type="checkbox"
                              checked={optionExport}
                              onChange={(e) => updateOptionExport(index, optionIndex, e.target.checked)}
                              className="w-3.5 h-3.5 text-blue-600 rounded focus:ring-blue-500"
                                    />
                            <span className="hidden sm:inline">Export</span>
                            <svg className="w-3.5 h-3.5 sm:hidden" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                          </label>
                          <button
                            type="button"
                            onClick={() => removeOption(index, optionIndex)}
                            disabled={(field.options?.length || 0) <= 1}
                            className="p-2 text-red-500 hover:text-red-700 disabled:opacity-30"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => addOption(index)}
                    className="mt-2 text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Aggiungi opzione
                  </button>
                </div>
              )}

              {/* Required checkbox and Include in Export for text fields */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id={`required-${field.id}`}
                    checked={field.required}
                    onChange={(e) => updateField(index, { required: e.target.checked })}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor={`required-${field.id}`} className="text-sm text-gray-700">
                    Campo obbligatorio
                  </label>
                </div>

                {/* Include in Export checkbox - only for text/textarea fields */}
                {(field.type === 'text' || field.type === 'textarea') && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`export-${field.id}`}
                      checked={field.includeInExport || false}
                      onChange={(e) => updateField(index, { includeInExport: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <label htmlFor={`export-${field.id}`} className="text-sm text-gray-700" title="La risposta apparirà nella lista partecipanti esportata (CSV/PDF)">
                      Includi in export partecipanti
                    </label>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add Field Button */}
      <button
        type="button"
        onClick={addField}
        className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-blue-400 hover:text-blue-600 transition-colors"
      >
        + Aggiungi campo
      </button>
    </div>
  );
}

export default FormBuilder;
